import React, { useEffect, useRef, useState } from 'react'
import { getTransactionStatus, selectTransactionData } from '../../../slices/TransactionIdSlice';
import { getNewUrlWithAllQueryParams, getStorage, isEmpty, isUnresolvedPaymentStatus, updateStorage } from '../../../Utils/utils';
import { startAutoRefund } from '../../../slices/AutoRefundSlice';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../loadingScreen/Loading';
import { useTranslation } from 'react-i18next';
import { paymentSession, getRetryPolicyRemaining } from '../../../slices/PaymentSessionDetailsSlice';
import ErrorModal from '../modals/errorModal/ErrorModal';
import { InfoCircleOutlined } from "@ant-design/icons";
import { showFailedModal } from '../../../slices/PaymentInitiationSlice';

const PspCheckoutHandler = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const parsedUrl = new URL(window.location.href);
    const params = new URLSearchParams(parsedUrl.search);
    const sessionToken = params.get('sessionToken');
    const transactionId = params.get('transactionId');
    const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
    const transactionData = useSelector((state) => selectTransactionData(state));
    // set when the auto refund loader gave up waiting for a final status of this payment
    const isAutoRefundUnresolved = useSelector((state) => !!transactionId && state.autoRefund.unresolvedTransactionId === transactionId);
    // set while the auto refund loader waits for a final status of this payment
    const isAutoRefundActive = useSelector((state) => !!transactionId && state.autoRefund.activeTransactionId === transactionId);
    const hasTriedAutoRefundRef = useRef(false);
    // auto refund is off or did not start: the page carries on as before
    const [isAutoRefundSkipped, setIsAutoRefundSkipped] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    useEffect(() => {
        if (!isEmpty(sessionDetails) && !isEmpty(transactionData)) {
            const frontendReturnUrl = sessionDetails?.paymentDetails?.frontendReturnUrl;
            // the auto refund loader is waiting for the final status: the result is routed when it ends
            if (isAutoRefundActive) {
                return;
            }
            // the PSP webhook can be late: the auto refund loader waits for the final status before a result is shown
            if (isUnresolvedPaymentStatus(transactionData?.status) && !isAutoRefundUnresolved && !hasTriedAutoRefundRef.current) {
                hasTriedAutoRefundRef.current = true;
                dispatch(startAutoRefund(transactionId)).then((isAutoRefundStarted) => {
                    if (!isAutoRefundStarted) setIsAutoRefundSkipped(true);
                });
                return;
            }
            if ((transactionData?.status === "FAILED" || transactionData?.status === "Rejected" || (isAutoRefundUnresolved && isUnresolvedPaymentStatus(transactionData?.status)))) {
                setShowErrorModal(true);
            } else if (frontendReturnUrl) {
                const returnUrl = getNewUrlWithAllQueryParams(frontendReturnUrl, null, 'token');
                window.location.href = returnUrl;
            } else {
                window.location.replace(`/finalStatus`)
            }
        }
    }, [sessionDetails, transactionData, isAutoRefundUnresolved, isAutoRefundActive, isAutoRefundSkipped]);

    useEffect(() => {
        const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!isEmpty(sessionToken) && !isEmpty(transactionId)) {
            const sessionTokenFromStorage = getStorage("session", "tkn");
            const transactionIdFromStorage = getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
            if (isEmpty(sessionTokenFromStorage) && isEmpty(transactionIdFromStorage)) {
                updateStorage("session", "clear");
            }
            // non seemless flow
            console.log("token-----sessionToken", sessionToken)

            updateStorage("session", "update", "tkn", sessionToken);
            updateStorage("session", "update", "t_id", transactionId);
            dispatch(paymentSession({ token: sessionToken, browserTimeZone }));
            dispatch(getRetryPolicyRemaining({ token: sessionToken }));
            dispatch(getTransactionStatus({ token: sessionToken, transactionId: transactionId }));
        }
    }, [sessionToken, transactionId]);

    const handleOkClick = () => {
        const frontendReturnUrl = sessionDetails?.paymentDetails?.frontendReturnUrl;
        const frontendBackUrl = sessionDetails?.paymentDetails?.frontendBackUrl;
        window.location.href = frontendReturnUrl || frontendBackUrl;
        setShowErrorModal(false);
    }

    return (
        <>
            <Loading
                text={`${t("LOADING")}...`}
                subText=""
            />
            {
                showErrorModal && (
                    <ErrorModal
                        icon={
                            <InfoCircleOutlined style={{ color: "#FF4D4F", fontSize: "22px" }} />
                        }
                        title={t("PAYMENT_FAILED")}
                        desc={t("PAYMENT_RETRY_NOTICE")}
                        showModal={showFailedModal}
                        primaryOnClick={() => handleOkClick()}
                        primaryBtnText={t("RETRY")}
                    />
                )
            }
        </>
    )
}

export default PspCheckoutHandler